import { forwardRef } from 'react';
import classNames from 'classnames/bind';

import { ChevronDownIcon } from '@autopass-web/mochi-icons';

import styles from './mochi-select.module.scss';

const cx = classNames.bind(styles);

interface MochiSelectProps {
  onChange: (value: string) => void;
  value: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  labelFor?: string;
  variant?: 'round' | 'normal';
  errorMessage?: string | null | undefined;
  isDarkMode?: boolean;
  onFocus?: React.DOMAttributes<HTMLSelectElement>['onFocus'];
}

export const MochiSelect = forwardRef<HTMLSelectElement, MochiSelectProps>(
  (
    {
      labelFor,
      options,
      value: _value,
      placeholder,
      variant = 'round',
      errorMessage,
      isDarkMode = false,
      onChange,
      onFocus,
    },
    ref,
  ) => {
    const value = _value || '';

    const isInvalid = !!errorMessage;

    const selectClass = cx({
      select: true,
      [variant]: true,
      'dark-mode': isDarkMode,
      empty: !value,
      invalid: isInvalid,
      'with-placeholder': !value,
    });

    const errorMessageClass = cx({
      'error-message': true,
      [variant]: true,
    });

    return (
      <div className={cx('container')}>
        <div className={cx('select-container')}>
          <ChevronDownIcon
            color={isDarkMode ? '#999999' : '#333333'}
            className={cx('chevron-down-icon')}
          />
          <select
            className={selectClass}
            id={labelFor}
            ref={ref}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
          >
            <option value="" disabled hidden>
              {placeholder}
            </option>
            {options.map((item) => {
              return (
                <option value={item.value} key={item.value}>
                  {item.label}
                </option>
              );
            })}
          </select>
        </div>
        {errorMessage && (
          <div className={errorMessageClass}>{errorMessage}</div>
        )}
      </div>
    );
  },
);

MochiSelect.displayName = 'MochiSelect';
